import { QueryClient } from '@tanstack/react-query';

/**
 * Global QueryClient instance for TanStack Query.
 *
 * Configuration optimized for:
 * - Static reference data (courses, plans, levels) - longer stale time
 * - Dynamic user data - shorter stale time with background refetch
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry failed requests twice
      retry: 2,
      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});

/**
 * Query key factory for type-safe cache management.
 * Use these keys for invalidation and cache updates.
 *
 * @example
 * // Invalidate all courses
 * queryClient.invalidateQueries({ queryKey: queryKeys.courses.all });
 *
 * // Invalidate specific course
 * queryClient.invalidateQueries({ queryKey: queryKeys.courses.detail(courseId) });
 */
export const queryKeys = {
  // Static reference data
  courses: {
    all: ['courses'] as const,
    detail: (id: string | number) => ['courses', id] as const,
  },
  plans: {
    all: ['plans'] as const,
    byLocale: (locale: string) => ['plans', { locale }] as const,
  },
  levels: {
    all: ['levels'] as const,
  },
  holidays: {
    all: ['holidays'] as const,
  },

  // Auth endpoints
  auth: {
    currentUser: ['auth', 'currentUser'] as const,
    refresh: ['auth', 'refresh'] as const,
  },

  // Future expansion - uncomment as you migrate these reducers
  // tutors: {
  //   all: ['tutors'] as const,
  //   detail: (id: string) => ['tutors', id] as const,
  //   timeslots: (id: string) => ['tutors', id, 'timeslots'] as const,
  // },
  // lectures: {
  //   all: ['lectures'] as const,
  //   detail: (id: string | number) => ['lectures', id] as const,
  // },
} as const;
